import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, Brain, Sparkles, Target, TrendingUp } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as ss from 'simple-statistics';
import { ParsedWorksheet, WorksheetColumn } from './worksheet';

interface PredictiveAnalyticsProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

type TaskMode = 'auto' | 'regression' | 'classification';
type EffectiveTask = 'regression' | 'classification';

type PreparedRegressionRow = {
  rowIndex: number;
  features: number[];
  target: number;
};

type PreparedClassificationRow = {
  rowIndex: number;
  features: number[];
  target: string;
};

type RegressionResult = {
  kind: 'regression';
  name: string;
  mae: number;
  rmse: number;
  r2: number;
  actual: number[];
  predictions: number[];
  rowIndices: number[];
  notes: string;
};

type ClassificationResult = {
  kind: 'classification';
  name: string;
  accuracy: number;
  precision: number;
  recall: number;
  f1: number;
  actual: string[];
  predictions: string[];
  rowIndices: number[];
  labels: string[];
  notes: string;
};

const formatNumber = (value: number, digits = 4) => (Number.isFinite(value) ? value.toFixed(digits) : '—');

const inferTaskType = (column?: WorksheetColumn): EffectiveTask => {
  if (!column) return 'regression';
  const isFullyNumeric = column.nonEmptyCount > 0 && column.numericCount === column.nonEmptyCount;
  if (!isFullyNumeric) return 'classification';
  const uniqueCount = column.uniqueValues.length;
  return uniqueCount <= 8 ? 'classification' : 'regression';
};

const seededShuffle = <T,>(items: T[]) => {
  let seed = 42;
  const random = () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }
  return copy;
};

const splitRows = <T,>(rows: T[], trainFraction: number) => {
  const shuffled = seededShuffle(rows);
  const desiredTrainCount = Math.floor(shuffled.length * trainFraction);
  const trainCount = Math.max(2, Math.min(shuffled.length - 1, desiredTrainCount));
  return {
    train: shuffled.slice(0, trainCount),
    test: shuffled.slice(trainCount),
  };
};

const transpose = (matrix: number[][]) => matrix[0].map((_, columnIndex) => matrix.map((row) => row[columnIndex]));

const matrixMultiply = (a: number[][], b: number[][]) => {
  return a.map((row) => b[0].map((_, columnIndex) => row.reduce((sum, value, rowIndex) => sum + value * b[rowIndex][columnIndex], 0)));
};

const matrixVectorMultiply = (matrix: number[][], vector: number[]) => {
  return matrix.map((row) => row.reduce((sum, value, index) => sum + value * vector[index], 0));
};

const invertMatrix = (matrix: number[][]) => {
  const size = matrix.length;
  const identity = Array.from({ length: size }, (_, rowIndex) =>
    Array.from({ length: size }, (_, columnIndex) => (rowIndex === columnIndex ? 1 : 0)),
  );
  const augmented = matrix.map((row, rowIndex) => [...row, ...identity[rowIndex]]);

  for (let pivotIndex = 0; pivotIndex < size; pivotIndex += 1) {
    let maxRow = pivotIndex;
    for (let rowIndex = pivotIndex + 1; rowIndex < size; rowIndex += 1) {
      if (Math.abs(augmented[rowIndex][pivotIndex]) > Math.abs(augmented[maxRow][pivotIndex])) {
        maxRow = rowIndex;
      }
    }

    [augmented[pivotIndex], augmented[maxRow]] = [augmented[maxRow], augmented[pivotIndex]];
    const pivot = augmented[pivotIndex][pivotIndex];
    if (Math.abs(pivot) < 1e-10) {
      throw new Error('Singular matrix');
    }

    for (let columnIndex = 0; columnIndex < size * 2; columnIndex += 1) {
      augmented[pivotIndex][columnIndex] /= pivot;
    }

    for (let rowIndex = 0; rowIndex < size; rowIndex += 1) {
      if (rowIndex === pivotIndex) continue;
      const factor = augmented[rowIndex][pivotIndex];
      for (let columnIndex = 0; columnIndex < size * 2; columnIndex += 1) {
        augmented[rowIndex][columnIndex] -= factor * augmented[pivotIndex][columnIndex];
      }
    }
  }

  return augmented.map((row) => row.slice(size));
};

const fitLinearModel = (rows: PreparedRegressionRow[]) => {
  const x = rows.map((row) => [1, ...row.features]);
  const y = rows.map((row) => row.target);
  const xt = transpose(x);
  const xtx = matrixMultiply(xt, x);
  for (let index = 0; index < xtx.length; index += 1) {
    xtx[index][index] += 1e-8;
  }
  const coefficients = matrixVectorMultiply(invertMatrix(xtx), matrixVectorMultiply(xt, y));
  return {
    coefficients,
    predict: (features: number[]) => coefficients[0] + features.reduce((sum, value, index) => sum + coefficients[index + 1] * value, 0),
  };
};

const fitRegressionStump = (rows: PreparedRegressionRow[]) => {
  let best:
    | {
        featureIndex: number;
        threshold: number;
        leftMean: number;
        rightMean: number;
        error: number;
      }
    | null = null;

  const featureCount = rows[0]?.features.length ?? 0;
  for (let featureIndex = 0; featureIndex < featureCount; featureIndex += 1) {
    const sorted = [...rows].sort((a, b) => a.features[featureIndex] - b.features[featureIndex]);
    for (let index = 1; index < sorted.length; index += 1) {
      const left = sorted.slice(0, index);
      const right = sorted.slice(index);
      const leftValue = left[left.length - 1].features[featureIndex];
      const rightValue = right[0].features[featureIndex];
      if (leftValue === rightValue) continue;
      const threshold = (leftValue + rightValue) / 2;
      const leftMean = ss.mean(left.map((row) => row.target));
      const rightMean = ss.mean(right.map((row) => row.target));
      const error = left.reduce((sum, row) => sum + (row.target - leftMean) ** 2, 0)
        + right.reduce((sum, row) => sum + (row.target - rightMean) ** 2, 0);

      if (!best || error < best.error) {
        best = { featureIndex, threshold, leftMean, rightMean, error };
      }
    }
  }

  if (!best) {
    throw new Error('No stump split found');
  }

  return {
    ...best,
    predict: (features: number[]) => (features[best.featureIndex] <= best.threshold ? best.leftMean : best.rightMean),
  };
};

const computeRegressionMetrics = (actual: number[], predictions: number[]) => {
  const residuals = actual.map((value, index) => value - predictions[index]);
  const mae = ss.mean(residuals.map((value) => Math.abs(value)));
  const rmse = Math.sqrt(ss.mean(residuals.map((value) => value ** 2)));
  const meanActual = ss.mean(actual);
  const totalVariance = actual.reduce((sum, value) => sum + (value - meanActual) ** 2, 0);
  const residualVariance = residuals.reduce((sum, value) => sum + value ** 2, 0);
  return {
    mae,
    rmse,
    r2: totalVariance === 0 ? 0 : 1 - residualVariance / totalVariance,
  };
};

const majorityLabel = (labels: string[]) => {
  const counts = labels.reduce<Record<string, number>>((acc, label) => {
    acc[label] = (acc[label] ?? 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
};

const fitNearestCentroid = (rows: PreparedClassificationRow[]) => {
  const grouped = rows.reduce<Record<string, number[][]>>((acc, row) => {
    acc[row.target] = acc[row.target] ?? [];
    acc[row.target].push(row.features);
    return acc;
  }, {});

  const centroids = Object.fromEntries(
    Object.entries(grouped).map(([label, vectors]) => [
      label,
      vectors[0].map((_, featureIndex) => ss.mean(vectors.map((vector) => vector[featureIndex]))),
    ]),
  );

  return {
    centroids,
    predict: (features: number[]) => {
      return Object.entries(centroids).sort((a, b) => {
        const distanceA = Math.sqrt(a[1].reduce((sum, value, index) => sum + (features[index] - value) ** 2, 0));
        const distanceB = Math.sqrt(b[1].reduce((sum, value, index) => sum + (features[index] - value) ** 2, 0));
        return distanceA - distanceB;
      })[0]?.[0] ?? '';
    },
  };
};

const fitClassificationStump = (rows: PreparedClassificationRow[]) => {
  let best:
    | {
        featureIndex: number;
        threshold: number;
        leftLabel: string;
        rightLabel: string;
        accuracy: number;
      }
    | null = null;

  const featureCount = rows[0]?.features.length ?? 0;
  for (let featureIndex = 0; featureIndex < featureCount; featureIndex += 1) {
    const sorted = [...rows].sort((a, b) => a.features[featureIndex] - b.features[featureIndex]);
    for (let index = 1; index < sorted.length; index += 1) {
      const left = sorted.slice(0, index);
      const right = sorted.slice(index);
      const leftValue = left[left.length - 1].features[featureIndex];
      const rightValue = right[0].features[featureIndex];
      if (leftValue === rightValue) continue;

      const threshold = (leftValue + rightValue) / 2;
      const leftLabel = majorityLabel(left.map((row) => row.target));
      const rightLabel = majorityLabel(right.map((row) => row.target));
      const correct = left.filter((row) => row.target === leftLabel).length + right.filter((row) => row.target === rightLabel).length;
      const accuracy = correct / sorted.length;

      if (!best || accuracy > best.accuracy) {
        best = { featureIndex, threshold, leftLabel, rightLabel, accuracy };
      }
    }
  }

  if (!best) {
    throw new Error('No stump split found');
  }

  return {
    ...best,
    predict: (features: number[]) => (features[best.featureIndex] <= best.threshold ? best.leftLabel : best.rightLabel),
  };
};

const computeClassificationMetrics = (actual: string[], predictions: string[]) => {
  const labels = Array.from(new Set([...actual, ...predictions]));
  const accuracy = actual.filter((value, index) => value === predictions[index]).length / actual.length;

  const perLabel = labels.map((label) => {
    let tp = 0;
    let fp = 0;
    let fn = 0;
    actual.forEach((value, index) => {
      const predicted = predictions[index];
      if (value === label && predicted === label) tp += 1;
      if (value !== label && predicted === label) fp += 1;
      if (value === label && predicted !== label) fn += 1;
    });
    const precision = tp + fp === 0 ? 0 : tp / (tp + fp);
    const recall = tp + fn === 0 ? 0 : tp / (tp + fn);
    const f1 = precision + recall === 0 ? 0 : (2 * precision * recall) / (precision + recall);
    return { precision, recall, f1 };
  });

  return {
    accuracy,
    precision: ss.mean(perLabel.map((item) => item.precision)),
    recall: ss.mean(perLabel.map((item) => item.recall)),
    f1: ss.mean(perLabel.map((item) => item.f1)),
    labels,
  };
};

export const PredictiveAnalytics: React.FC<PredictiveAnalyticsProps> = ({ worksheet, selectedColumnId }) => {
  const targetCandidates = useMemo(() => worksheet.columns.filter((column) => column.nonEmptyCount > 0), [worksheet.columns]);
  const defaultTargetId = selectedColumnId && targetCandidates.some((column) => column.id === selectedColumnId)
    ? selectedColumnId
    : targetCandidates[targetCandidates.length - 1]?.id ?? '';

  const [targetColumnId, setTargetColumnId] = useState(defaultTargetId);
  const [taskMode, setTaskMode] = useState<TaskMode>('auto');
  const [trainPercent, setTrainPercent] = useState('75');
  const [featureColumnIds, setFeatureColumnIds] = useState<string[]>([]);

  const targetColumn = targetCandidates.find((column) => column.id === targetColumnId) ?? targetCandidates[targetCandidates.length - 1];
  const effectiveTask = taskMode === 'auto' ? inferTaskType(targetColumn) : taskMode;
  const featureCandidates = useMemo(
    () => worksheet.numericColumns.filter((column) => column.id !== targetColumn?.id),
    [targetColumn?.id, worksheet.numericColumns],
  );

  useEffect(() => {
    setTargetColumnId((current) => {
      if (current && targetCandidates.some((column) => column.id === current)) return current;
      return defaultTargetId;
    });
  }, [defaultTargetId, targetCandidates]);

  useEffect(() => {
    setFeatureColumnIds((current) => {
      const valid = current.filter((columnId) => featureCandidates.some((column) => column.id === columnId));
      if (valid.length > 0) return valid;
      return featureCandidates.slice(0, Math.min(4, featureCandidates.length)).map((column) => column.id);
    });
  }, [featureCandidates]);

  const featureColumns = featureCandidates.filter((column) => featureColumnIds.includes(column.id));

  const preparedRows = useMemo<Array<PreparedRegressionRow | PreparedClassificationRow>>(() => {
    if (!targetColumn || featureColumns.length === 0) return [];

    const rows: Array<PreparedRegressionRow | PreparedClassificationRow> = [];
    worksheet.rows.forEach((_, rowIndex) => {
      const features = featureColumns.map((column) => Number(column.values[rowIndex] ?? ''));
      if (features.some((value) => !Number.isFinite(value))) return;

      const rawTarget = (targetColumn.values[rowIndex] ?? '').trim();
      if (!rawTarget) return;

      if (effectiveTask === 'regression') {
        const numericTarget = Number(rawTarget);
        if (!Number.isFinite(numericTarget)) return;
        rows.push({ rowIndex: rowIndex + 1, features, target: numericTarget });
        return;
      }

      rows.push({ rowIndex: rowIndex + 1, features, target: rawTarget });
    });

    return rows;
  }, [effectiveTask, featureColumns, targetColumn, worksheet.rows]);

  const modelResults = useMemo(() => {
    const trainFraction = Math.min(0.9, Math.max(0.5, (Number(trainPercent) || 75) / 100));
    if (preparedRows.length < 8 || featureColumns.length === 0) {
      return null;
    }

    if (effectiveTask === 'regression') {
      const rows = preparedRows as PreparedRegressionRow[];
      const { train, test } = splitRows(rows, trainFraction);
      if (test.length < 2) return null;

      const baselineMean = ss.mean(train.map((row) => row.target));
      const actual = test.map((row) => row.target);
      const rowIndices = test.map((row) => row.rowIndex);
      const results: RegressionResult[] = [];

      const baselinePredictions = test.map(() => baselineMean);
      results.push({
        kind: 'regression',
        name: 'Mean baseline',
        ...computeRegressionMetrics(actual, baselinePredictions),
        actual,
        predictions: baselinePredictions,
        rowIndices,
        notes: 'Predicts the training-set mean for every holdout row.',
      });

      if (train.length > featureColumns.length + 1) {
        try {
          const linearModel = fitLinearModel(train);
          const predictions = test.map((row) => linearModel.predict(row.features));
          results.push({
            kind: 'regression',
            name: 'Linear regression',
            ...computeRegressionMetrics(actual, predictions),
            actual,
            predictions,
            rowIndices,
            notes: `Fits an intercept plus ${featureColumns.length} numeric predictor${featureColumns.length === 1 ? '' : 's'}.`,
          });
        } catch {}
      }

      try {
        const stumpModel = fitRegressionStump(train);
        const predictions = test.map((row) => stumpModel.predict(row.features));
        results.push({
          kind: 'regression',
          name: 'Regression tree stump',
          ...computeRegressionMetrics(actual, predictions),
          actual,
          predictions,
          rowIndices,
          notes: `Best one-split tree found on ${featureColumns[stumpModel.featureIndex]?.name || 'a predictor'} at ${formatNumber(stumpModel.threshold, 3)}.`,
        });
      } catch {}

      const best = [...results].sort((a, b) => a.rmse - b.rmse)[0];
      return { task: 'regression' as const, trainCount: train.length, testCount: test.length, results, best };
    }

    const rows = preparedRows as PreparedClassificationRow[];
    const uniqueLabels = Array.from(new Set(rows.map((row) => row.target)));
    if (uniqueLabels.length < 2) return null;

    const { train, test } = splitRows(rows, trainFraction);
    if (test.length < 2) return null;

    const actual = test.map((row) => row.target);
    const rowIndices = test.map((row) => row.rowIndex);
    const results: ClassificationResult[] = [];

    const majority = majorityLabel(train.map((row) => row.target));
    const baselinePredictions = test.map(() => majority);
    const baselineMetrics = computeClassificationMetrics(actual, baselinePredictions);
    results.push({
      kind: 'classification',
      name: 'Majority baseline',
      ...baselineMetrics,
      actual,
      predictions: baselinePredictions,
      rowIndices,
      notes: `Always predicts the largest training class: ${majority}.`,
    });

    try {
      const centroidModel = fitNearestCentroid(train);
      const predictions = test.map((row) => centroidModel.predict(row.features));
      const metrics = computeClassificationMetrics(actual, predictions);
      results.push({
        kind: 'classification',
        name: 'Nearest centroid',
        ...metrics,
        actual,
        predictions,
        rowIndices,
        notes: 'Classifies each holdout row by the closest class center in feature space.',
      });
    } catch {}

    try {
      const stumpModel = fitClassificationStump(train);
      const predictions = test.map((row) => stumpModel.predict(row.features));
      const metrics = computeClassificationMetrics(actual, predictions);
      results.push({
        kind: 'classification',
        name: 'Decision stump',
        ...metrics,
        actual,
        predictions,
        rowIndices,
        labels: metrics.labels,
        notes: `Uses a single split on ${featureColumns[stumpModel.featureIndex]?.name || 'a predictor'} at ${formatNumber(stumpModel.threshold, 3)}.`,
      });
    } catch {}

    const best = [...results].sort((a, b) => (b.accuracy - a.accuracy) || (b.f1 - a.f1))[0];
    return { task: 'classification' as const, trainCount: train.length, testCount: test.length, results, best };
  }, [effectiveTask, featureColumns, preparedRows, trainPercent]);

  const regressionChartData = useMemo(() => {
    if (!modelResults || modelResults.task !== 'regression') return [];
    return modelResults.best.actual.map((value, index) => ({
      actual: value,
      predicted: modelResults.best.predictions[index],
      row: modelResults.best.rowIndices[index],
    }));
  }, [modelResults]);

  const classificationChartData = useMemo(() => {
    if (!modelResults || modelResults.task !== 'classification') return [];
    const labels = modelResults.best.labels;
    return labels.map((label) => ({
      label,
      actual: modelResults.best.actual.filter((value) => value === label).length,
      predicted: modelResults.best.predictions.filter((value) => value === label).length,
    }));
  }, [modelResults]);

  if (worksheet.columns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No worksheet data</AlertTitle>
        <AlertDescription>Paste worksheet data to start comparing predictive models.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-purple-700 p-3 text-white">
          <Brain size={24} />
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge>Ready</Badge>
            <Badge variant="outline">AutoML preview</Badge>
          </div>
          <h2 className="text-2xl font-semibold text-foreground">Predictive Analytics</h2>
          <p className="text-muted-foreground">Compare quick predictive models on the current worksheet using holdout scoring for regression or classification.</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Target column</CardTitle></CardHeader>
          <CardContent><div className="text-lg font-semibold">{targetColumn?.name || 'None selected'}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Detected task</CardTitle></CardHeader>
          <CardContent><div className="text-lg font-semibold capitalize">{effectiveTask}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Usable rows</CardTitle></CardHeader>
          <CardContent><div className="text-lg font-semibold">{preparedRows.length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Candidate models</CardTitle></CardHeader>
          <CardContent><div className="text-lg font-semibold">3</div></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Model setup</CardTitle>
          <CardDescription>Pick a target, choose regression or classification, then select the numeric predictors to compare.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div className="space-y-2">
              <Label>Target variable</Label>
              <Select value={targetColumnId} onValueChange={setTargetColumnId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {targetCandidates.map((column) => (
                    <SelectItem key={column.id} value={column.id}>
                      {column.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Task type</Label>
              <Select value={taskMode} onValueChange={(value: TaskMode) => setTaskMode(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  <SelectItem value="regression">Regression</SelectItem>
                  <SelectItem value="classification">Classification</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Training split (%)</Label>
              <Input value={trainPercent} onChange={(event) => setTrainPercent(event.target.value)} />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label>Numeric predictors</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFeatureColumnIds(featureCandidates.map((column) => column.id))}
                disabled={featureCandidates.length === 0}
              >
                Use all predictors
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {featureCandidates.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                  Add at least one numeric predictor column besides the target.
                </div>
              ) : featureCandidates.map((column) => {
                const isSelected = featureColumnIds.includes(column.id);
                return (
                  <Button
                    key={column.id}
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFeatureColumnIds((current) => (
                      current.includes(column.id)
                        ? current.filter((columnId) => columnId !== column.id)
                        : [...current, column.id]
                    ))}
                  >
                    {column.name}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {!targetColumn || featureColumns.length === 0 ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Select a target and at least one numeric predictor</AlertTitle>
          <AlertDescription>Predictive Analytics needs one output column plus at least one numeric feature column to compare models.</AlertDescription>
        </Alert>
      ) : !modelResults ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Need a larger usable dataset</AlertTitle>
          <AlertDescription>Make sure the worksheet has enough complete rows for the selected target and predictors. A good starting point is at least 8 usable rows and at least 2 holdout rows.</AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Best current model
                </CardTitle>
                <CardDescription>{modelResults.best.notes}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border border-border/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Model</div>
                  <div className="mt-2 text-lg font-semibold">{modelResults.best.name}</div>
                </div>
                <div className="rounded-xl border border-border/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Train / test</div>
                  <div className="mt-2 text-lg font-semibold">{modelResults.trainCount} / {modelResults.testCount}</div>
                </div>
                {modelResults.task === 'regression' ? (
                  <>
                    <div className="rounded-xl border border-border/70 p-4">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">RMSE</div>
                      <div className="mt-2 text-lg font-semibold">{formatNumber(modelResults.best.rmse)}</div>
                    </div>
                    <div className="rounded-xl border border-border/70 p-4">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">R²</div>
                      <div className="mt-2 text-lg font-semibold">{formatNumber(modelResults.best.r2)}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-xl border border-border/70 p-4">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Accuracy</div>
                      <div className="mt-2 text-lg font-semibold">{formatNumber(modelResults.best.accuracy)}</div>
                    </div>
                    <div className="rounded-xl border border-border/70 p-4">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Macro F1</div>
                      <div className="mt-2 text-lg font-semibold">{formatNumber(modelResults.best.f1)}</div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Feature pack</CardTitle>
                <CardDescription>Numeric predictors currently included in the comparison.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {featureColumns.map((column) => (
                  <Badge key={column.id} variant="secondary">{column.name}</Badge>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Model comparison</CardTitle>
              <CardDescription>Holdout scoring across quick baseline, linear / centroid, and tree-style models.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Model</TableHead>
                    {modelResults.task === 'regression' ? (
                      <>
                        <TableHead className="text-right">MAE</TableHead>
                        <TableHead className="text-right">RMSE</TableHead>
                        <TableHead className="text-right">R²</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="text-right">Accuracy</TableHead>
                        <TableHead className="text-right">Precision</TableHead>
                        <TableHead className="text-right">Recall</TableHead>
                        <TableHead className="text-right">F1</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modelResults.results.map((result) => (
                    <TableRow key={result.name}>
                      <TableCell>
                        <div className="font-medium">{result.name}</div>
                        <div className="text-xs text-muted-foreground">{result.notes}</div>
                      </TableCell>
                      {result.kind === 'regression' ? (
                        <>
                          <TableCell className="text-right font-mono">{formatNumber(result.mae)}</TableCell>
                          <TableCell className="text-right font-mono">{formatNumber(result.rmse)}</TableCell>
                          <TableCell className="text-right font-mono">{formatNumber(result.r2)}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-right font-mono">{formatNumber(result.accuracy)}</TableCell>
                          <TableCell className="text-right font-mono">{formatNumber(result.precision)}</TableCell>
                          <TableCell className="text-right font-mono">{formatNumber(result.recall)}</TableCell>
                          <TableCell className="text-right font-mono">{formatNumber(result.f1)}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="grid gap-4 xl:grid-cols-5">
            <Card className="xl:col-span-3">
              <CardHeader>
                <CardTitle className="text-lg">{modelResults.task === 'regression' ? 'Predicted vs actual' : 'Actual vs predicted class counts'}</CardTitle>
                <CardDescription>
                  {modelResults.task === 'regression'
                    ? 'Use this holdout scatter to gauge how tightly predictions line up with observed values.'
                    : 'Compare how often each class appears in the holdout set versus the best model predictions.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    {modelResults.task === 'regression' ? (
                      <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" dataKey="actual" name="Actual" />
                        <YAxis type="number" dataKey="predicted" name="Predicted" />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Scatter name="Holdout rows" data={regressionChartData} fill="hsl(var(--primary))" />
                      </ScatterChart>
                    ) : (
                      <BarChart data={classificationChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="actual" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="predicted" fill="hsl(var(--muted-foreground))" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Why this is useful</CardTitle>
                <CardDescription>A fast worksheet-native way to demo predictive capability without leaving MapleStats.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <div className="rounded-lg border border-border/70 p-3">
                  <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
                    <Target className="h-4 w-4" />
                    Automatic task framing
                  </div>
                  MapleStats can treat the target as regression or classification, or let you force the mode manually.
                </div>
                <div className="rounded-lg border border-border/70 p-3">
                  <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
                    <Brain className="h-4 w-4" />
                    Multiple quick models
                  </div>
                  Compare a baseline with linear / centroid style models and simple tree-style logic on the same holdout split.
                </div>
                <div className="rounded-lg border border-border/70 p-3">
                  <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
                    <TrendingUp className="h-4 w-4" />
                    Honest scoring
                  </div>
                  The scores shown are measured on holdout rows rather than on the rows used to fit the model.
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Holdout preview</CardTitle>
              <CardDescription>The first few holdout rows scored by the best model.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Actual</TableHead>
                    <TableHead>Predicted</TableHead>
                    <TableHead className="text-right">{modelResults.task === 'regression' ? 'Residual' : 'Match'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modelResults.best.rowIndices.slice(0, 12).map((rowIndex, index) => (
                    <TableRow key={`${rowIndex}-${index}`}>
                      <TableCell>{rowIndex}</TableCell>
                      <TableCell className="font-mono">
                        {modelResults.task === 'regression'
                          ? formatNumber(modelResults.best.actual[index])
                          : modelResults.best.actual[index]}
                      </TableCell>
                      <TableCell className="font-mono">
                        {modelResults.task === 'regression'
                          ? formatNumber(modelResults.best.predictions[index])
                          : modelResults.best.predictions[index]}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {modelResults.task === 'regression'
                          ? formatNumber(modelResults.best.actual[index] - modelResults.best.predictions[index])
                          : modelResults.best.actual[index] === modelResults.best.predictions[index] ? '✓' : '✗'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
